import React, { useState, useEffect } from 'react';
import { createUnit, updateUnit } from '../../services/unitsService';

const UnitForm = ({ isOpen, onClose, unit = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    symbol: '',
    description: '',
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (unit) {
      // Modo edición
      setFormData({
        code: unit.code || '',
        name: unit.name || '',
        symbol: unit.symbol || '',
        description: unit.description || '',
        is_active: unit.is_active !== undefined ? unit.is_active : true,
      });
    } else {
      // Modo creación
      setFormData({
        code: '',
        name: '',
        symbol: '',
        description: '',
        is_active: true,
      });
    }
    setErrors({});
  }, [unit, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Para el código: alfanumérico, máximo 10 caracteres, convertir a mayúsculas
    if (name === 'code') {
      processedValue = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      if (processedValue.length > 10) {
        processedValue = processedValue.slice(0, 10);
      }
    }
    
    // Para el símbolo: convertir a minúsculas
    if (name === 'symbol') {
      processedValue = value;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue,
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'El código es obligatorio';
    } else if (formData.code.length > 10) {
      newErrors.code = 'El código no puede tener más de 10 caracteres';
    } else if (!/^[0-9A-Za-z]+$/.test(formData.code)) {
      newErrors.code = 'El código debe contener solo números y letras';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    } else if (formData.name.length > 100) {
      newErrors.name = 'El nombre no puede tener más de 100 caracteres';
    }

    if (!formData.symbol.trim()) {
      newErrors.symbol = 'El símbolo es obligatorio';
    } else if (formData.symbol.length > 20) {
      newErrors.symbol = 'El símbolo no puede tener más de 20 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      let response;
      if (unit) {
        // Actualizar
        response = await updateUnit(unit.id, formData);
      } else {
        // Crear
        response = await createUnit(formData);
      }

      if (response.success) {
        onSuccess(response.data);
        onClose();
      } else {
        // Manejar errores del servidor
        if (response.errors) {
          setErrors(response.errors);
        } else {
          setErrors({ submit: response.message || 'Error al guardar la unidad de medida' });
        }
      }
    } catch (error) {
      console.error('Error al guardar unidad:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Error al guardar la unidad de medida' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              {unit ? 'Editar Unidad de Medida' : 'Nueva Unidad de Medida'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {errors.submit && (
            <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md text-sm border border-red-200">
              {errors.submit}
            </div>
          )}

          {/* Código */}
          <div className="mb-4">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
              Código <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              maxLength={10}
              readOnly={!!unit} // Solo lectura en modo edición
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.code
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              } ${unit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Ej: KG, UN, L"
            />
            {errors.code && (
              <p className="mt-1 text-sm text-red-600">{errors.code}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {unit ? 'El código no se puede modificar' : 'Máximo 10 caracteres alfanuméricos'}
            </p>
          </div>

          {/* Nombre */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              maxLength={100}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.name
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              }`}
              placeholder="Ej: Kilogramo, Unidad, Litro"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Máximo 100 caracteres</p>
          </div>

          {/* Símbolo */}
          <div className="mb-4">
            <label htmlFor="symbol" className="block text-sm font-medium text-gray-700 mb-1">
              Símbolo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="symbol"
              name="symbol"
              value={formData.symbol}
              onChange={handleChange}
              maxLength={20}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.symbol
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              }`}
              placeholder="Ej: kg, u, L"
            />
            {errors.symbol && (
              <p className="mt-1 text-sm text-red-600">{errors.symbol}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Máximo 20 caracteres</p>
          </div>

          {/* Descripción */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Descripción de la unidad de medida (opcional)"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Estado Activo */}
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="ml-2 text-sm text-gray-700">Unidad activa</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Guardando...' : unit ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnitForm;

